import api from './api';
import type {
  Contact,
  CreateContactRequest,
  UpdateContactRequest,
  ContactFilters,
  PaginatedResponse,
} from '../types';

export const contactService = {
  async getAll(params?: ContactFilters): Promise<PaginatedResponse<Contact>> {
    const response = await api.get('/contacts', { params });
    const raw = response.data;
    // API returns { data: [...], pagination: { page, limit, total, totalPages } }
    // Normalize to PaginatedResponse shape that components expect
    if (raw && raw.pagination) {
      return {
        data: raw.data ?? [],
        page: raw.pagination.page ?? 1,
        pageSize: raw.pagination.limit ?? 20,
        total: raw.pagination.total ?? 0,
        totalPages: raw.pagination.totalPages ?? 1,
      };
    }
    if (Array.isArray(raw)) {
      return {
        data: raw,
        page: 1,
        pageSize: raw.length,
        total: raw.length,
        totalPages: 1,
      };
    }
    return {
      data: raw?.data ?? [],
      page: raw?.page ?? 1,
      pageSize: raw?.pageSize ?? raw?.limit ?? 20,
      total: raw?.total ?? 0,
      totalPages: raw?.totalPages ?? 1,
    };
  },

  async getById(id: string): Promise<Contact> {
    const response = await api.get<Contact>(`/contacts/${id}`);
    return response.data;
  },

  async getByClient(clientId: string): Promise<Contact[]> {
    const response = await api.get(`/clients/${clientId}/contacts`);
    const raw = response.data;
    // API returns array directly for client sub-routes
    if (Array.isArray(raw)) return raw;
    return raw?.data ?? [];
  },

  async create(data: CreateContactRequest): Promise<Contact> {
    const response = await api.post<Contact>('/contacts', data);
    return response.data;
  },

  async update(id: string, data: UpdateContactRequest): Promise<Contact> {
    const response = await api.put<Contact>(`/contacts/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/contacts/${id}`);
  },
};
